import { QueryClient } from "@tanstack/react-query";
import { LoaderFunctionArgs, redirect } from "react-router";

import { FileService, IngestionStatus } from "@/shared/domain/files";
import { fileQueryKeys } from "@/shared/domain/files/hooks/fileQueryKeys";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation";

/**
 * Decides what a file link opens:
 * 1. A legacy `/files/:fileId/:workspaceId` link opens that workspace in the visualizer.
 * 2. An ingested file opens in the visualizer, at the file's start.
 * 3. Any other file renders its own page.
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs): Promise<Response | null> => {
    const { fileId, workspaceId } = params;

    // TODO Should this redirect to 404 instead?
    if (!fileId) {
      throw new Error("File ID is required");
    }

    if (workspaceId) {
      return redirect(Goto.createVisualizerRoute(workspaceId));
    }

    const httpClient = await makeAuthenticatedHttpClient();
    const fileService = new FileService(httpClient);

    // Warm the cache slot `useFile` reads (keyed by the globally-unique fileId)
    // so the File route doesn't refetch on mount. `ensureQueryData` reuses a
    // fresh cached record / in-flight fetch rather than always refetching.
    const fileRecord = await queryClient.ensureQueryData({
      queryKey: fileQueryKeys.detail(fileId),
      queryFn: ({ signal }) =>
        fileService.getFileRecord(fileId, { abortSignal: signal }),
    });

    if (fileRecord.ingestion_status === IngestionStatus.Ingested) {
      return redirect(Goto.createFileVisualizerRoute(fileId));
    }

    return null;
  };
